/**
 * =============================================================================
 * Componente: AffiliateNotificationBell
 * Versão: 1.1.1 — 2026-05-01
 *   - v1.1.1: Fix de rolagem interna do popover (ScrollArea com altura fixa).
 *             cobertos pelas triggers (referrals/commissions/payouts).
 *   - v1.0.0: Sino com contagem, popover com 20 últimas, mark-as-read.
 * Descrição: Sino de notificações do painel do afiliado.
 * =============================================================================
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Inbox,
  UserPlus,
  CheckCircle2,
  XCircle,
  Hourglass,
  BadgeCheck,
  Wallet,
  Clock,
  AlertTriangle,
  PartyPopper,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  useAffiliateNotifications,
  useMarkAffiliateNotificationsRead,
} from '@/hooks/useAffiliateNotifications';

/** Formata "há X tempo" em pt-BR sem dependências externas. */
function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

/**
 * Mapeamento de tipo de notificação → ícone + classe de cor (semântica P&B).
 * Mantém compatibilidade com o tipo legado `commission_created`
 * (que era usado antes da migração 2026-05-01).
 */
const NOTIFICATION_ICON_MAP: Record<
  string,
  { icon: LucideIcon; className: string }
> = {
  // Indicações
  referral_created:    { icon: UserPlus,     className: 'text-sky-600 dark:text-sky-400' },
  referral_qualified:  { icon: UserPlus,     className: 'text-sky-600 dark:text-sky-400' },
  referral_converted:  { icon: CheckCircle2, className: 'text-emerald-600 dark:text-emerald-400' },
  referral_cancelled:  { icon: XCircle,      className: 'text-muted-foreground' },
  // Comissões
  commission_pending:  { icon: Hourglass,    className: 'text-amber-600 dark:text-amber-400' },
  commission_created:  { icon: Hourglass,    className: 'text-amber-600 dark:text-amber-400' }, // legado
  commission_approved: { icon: BadgeCheck,   className: 'text-emerald-600 dark:text-emerald-400' },
  commission_paid:     { icon: Wallet,       className: 'text-emerald-700 dark:text-emerald-300' },
  // Saques
  payout_processing:   { icon: Clock,        className: 'text-amber-600 dark:text-amber-400' },
  payout_paid:         { icon: CheckCircle2, className: 'text-emerald-600 dark:text-emerald-400' },
  payout_failed:       { icon: AlertTriangle, className: 'text-destructive' },
  // Boas-vindas
  welcome:             { icon: PartyPopper,  className: 'text-primary' },
};

function getNotificationIcon(type: string) {
  return NOTIFICATION_ICON_MAP[type] ?? { icon: Bell, className: 'text-muted-foreground' };
}

export function AffiliateNotificationBell() {
  const navigate = useNavigate();
  const { data: notifications = [], isLoading } = useAffiliateNotifications(20);
  const markRead = useMarkAffiliateNotificationsRead();

  // Contagem de não lidas para o badge.
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications],
  );

  const handleClickNotification = (id: string, isRead: boolean, actionUrl: string | null) => {
    if (!isRead) markRead.mutate([id]);
    if (actionUrl) navigate(actionUrl);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label="Notificações"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -top-0.5 -right-0.5 inline-flex items-center justify-center',
                'min-w-[16px] h-[16px] px-1 rounded-full',
                'bg-primary text-primary-foreground text-[10px] font-semibold tabular-nums',
              )}
              aria-label={`${unreadCount} não lidas`}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 overflow-hidden"
      >
        {/* Header do popover */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold">Notificações</span>
            {unreadCount > 0 && (
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {unreadCount} não lidas
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[12px] gap-1"
              onClick={() => markRead.mutate('all')}
              disabled={markRead.isPending}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Marcar lidas
            </Button>
          )}
        </div>

        {/* Lista */}
        {isLoading ? (
          <div className="py-10 text-center text-[12px] text-muted-foreground">
            Carregando…
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <Inbox className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <p className="text-sm font-medium">Sem notificações</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              Você será avisado por aqui.
            </p>
          </div>
        ) : (
          // ScrollArea do Radix exige ALTURA FIXA no container raiz para que o
          // viewport interno (height: 100%) calcule overflow e ative o scroll.
          // Usar `max-h` faz o container crescer junto com o conteúdo e a
          // barra nunca aparece. Por isso fixamos `h-[60vh]` com teto de
          // `480px` para telas grandes.
          <ScrollArea className="h-[60vh] max-h-[480px]">
            <ul className="divide-y divide-border/60">
              {notifications.map((n) => {
                const { icon: Icon, className: iconCls } = getNotificationIcon(n.type);
                return (
                  <li
                    key={n.id}
                    onClick={() => handleClickNotification(n.id, n.is_read, n.action_url)}
                    className={cn(
                      'group flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-colors',
                      'hover:bg-muted/50',
                      !n.is_read && 'bg-primary/[0.04]',
                    )}
                  >
                    {/* Ícone contextual por tipo de notificação */}
                    <div
                      aria-hidden
                      className={cn(
                        'mt-0.5 h-7 w-7 rounded-full shrink-0 flex items-center justify-center',
                        'bg-muted/60',
                      )}
                    >
                      <Icon className={cn('h-3.5 w-3.5', iconCls)} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p
                          className={cn(
                            'text-[13px] leading-snug line-clamp-1',
                            n.is_read ? 'text-foreground/80' : 'font-semibold',
                          )}
                        >
                          {n.title}
                        </p>
                        <span className="text-[10.5px] text-muted-foreground tabular-nums shrink-0 flex items-center gap-1">
                          {!n.is_read && (
                            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
                          )}
                          {timeAgo(n.created_at)}
                        </span>
                      </div>
                      {n.message && (
                        <p className="text-[12px] text-muted-foreground line-clamp-2 mt-0.5">
                          {n.message}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
